import { includesAny, includesOnly } from '@dte/otw-utils-core/src/lists/includesAny';
import { isNotEmpty } from '@dte/otw-utils-core/src/objects/isEmpty';
import type { CustomerReportDataV3, ReportAnswers } from '../../../../report/CustomerReport';
import {
	getFilteredReportAnswers,
	getSelectedAnswerStrings,
	isAnswerValueSelected,
	isQuestionAnswered,
} from '../../../../report/getQuestion';
import { EmergencyQuestionValue } from '../../../../report/questions/problemType/EmergencyQuestion';
import { EnergizedContact } from '../../../../report/questions/problemType/emergency/Contact/EnergizedContact';
import { FireQuestionValue } from '../../../../report/questions/problemType/emergency/Fire';
import { EquipmentProblemQuestionValue } from '../../../../report/questions/problemType/equipment/EquipmentProblemQuestion';
import { WireTypeQuestionValue } from '../../../../report/questions/problemType/equipment/overheadWire/WireTypeQuestion';
import { PoleIssueQuestionValue } from '../../../../report/questions/problemType/equipment/PoleIssueQuestion';
import { UndergroundWireIssueQuestionValue } from '../../../../report/questions/problemType/equipment/undergroundWire/UndergroundWireQuestionValue';
import { PowerIssue } from '../../../../report/questions/problemType/PowerIssue';
import { ReportQuestion, StandardAnswerValue } from '../../../../report/questions/ReportQuestion';
import { StandardObject } from '../../../interfaces/api/CustomerReport';
import type { PremiseDataDocument } from '../../../interfaces/containers/premises/PremiseData';
import { InterruptibleAppliances } from './ApplianceTypes';
import { hasInterruptibleService } from './hasInterruptibleService';
import { isEmergencyIssue } from './isEmergencyIssue';
import { isOutage } from './isOutage';
import { isPoliceFire, meetsPoliceFireStandbyCriteria } from './isPoliceFire';
import { isPowerIssue } from './isPowerIssue';
import { isPoleToBuildingDown, isWireDownOrWithinReachV3 } from './isWireDown';
import { isTreeTrimIssue } from './treeNeedsTrimmingV3';

const symptomMappings: Record<
	string,
	(answers: ReportAnswers, masterData?: PremiseDataDocument) => boolean | undefined
> = {};

symptomMappings['PPWD'] = (answers: ReportAnswers): boolean | undefined => {
	// Must be a pole to pole wire
	const poleToPole = isAnswerValueSelected(answers, ReportQuestion.WireType, WireTypeQuestionValue.PoleToPole);
	if (!poleToPole) {
		return false;
	}

	const policeFire = isPoliceFire(answers);
	if (policeFire) {
		return true;
	}

	// Wire must be down or within reach
	const downOrWithinReach = isWireDownOrWithinReachV3(answers);
	if (!downOrWithinReach) {
		return false;
	}

	return true;
};

symptomMappings['XCURR'] = (answers: ReportAnswers, masterData?: PremiseDataDocument): boolean | undefined => {
	const outage = isOutage(answers, masterData);
	if (!outage) {
		return false;
	}

	return true;
};

symptomMappings['WDPF'] = (answers: ReportAnswers): boolean | undefined => {
	// Reported by police / fire and they're standing by
	const policeFireStandingBy = meetsPoliceFireStandbyCriteria(answers);

	if (!policeFireStandingBy) {
		return false;
	}

	// Wire must be pole to pole for WDPF -- if it's pole to building
	const poleToPole = isAnswerValueSelected(answers, ReportQuestion.WireType, WireTypeQuestionValue.PoleToPole);
	const poleToBuilding = isAnswerValueSelected(
		answers,
		ReportQuestion.WireType,
		WireTypeQuestionValue.PoleToBuilding,
	);
	if (!poleToPole && !poleToBuilding) {
		return false;
	}

	// Wire is down
	const wireDown = isWireDownOrWithinReachV3(answers);
	if (wireDown) {
		return true;
	}

	return false;
};

symptomMappings['MISCPF'] = (answers: ReportAnswers): boolean => {
	// Reported by police / fire and they're standing by
	const policeFireStandingBy = meetsPoliceFireStandbyCriteria(answers);
	if (!policeFireStandingBy) {
		return false;
	}

	return true;
};

symptomMappings['SDXL'] = (answers: ReportAnswers, masterData: PremiseDataDocument | undefined): boolean => {
	// Can't be service down unless pole to building is selected
	const poleToBuildingDown = isPoleToBuildingDown(answers);
	if (!poleToBuildingDown) {
		return false;
	}

	// Power must be out
	const outage = isOutage(answers, masterData);
	if (!outage) {
		return false;
	}

	return true;
};

symptomMappings['SDLTSOK'] = (answers: ReportAnswers): boolean => {
	const poleToBuildingDown = isPoleToBuildingDown(answers);
	if (!poleToBuildingDown) {
		return false;
	}

	return true;
};

symptomMappings['TT'] = (answers: ReportAnswers): boolean => {
	// There's a tree on the wire that needs trimming
	const needsTrimming = isTreeTrimIssue(answers);
	if (!needsTrimming) {
		return false;
	}

	return true;
};

function isOnlyPoleDebris(answers: ReportAnswers): boolean {
	const debrisList = getSelectedAnswerStrings(answers, ReportQuestion.Debris);
	if (debrisList.includes(StandardObject.Pole)) {
		// Only consider as non-hazard if pole debris is the only issue
		return debrisList.length === 1;
	}

	return false;
}

function isNonHazard(answers: ReportAnswers, masterData: PremiseDataDocument | undefined): boolean {
	// Not a non-hazard if there's an emergency
	const emergency = isEmergencyIssue(answers);
	if (emergency) {
		return false;
	}

	// Not a non-hazard if there's a power quality issue
	const powerIssue = isPowerIssue(answers, masterData);
	if (powerIssue) {
		return false;
	}

	// TODO: May need more non-hazards
	const poleDebrisOnly = isOnlyPoleDebris(answers);
	if (poleDebrisOnly) {
		// If the only debris is a pole, we consider it a non-hazard
		return true;
	}

	// GLN "other issue" is considered a non-hazard
	const otherGLNIssue = isAnswerValueSelected(answers, ReportQuestion.GLNProblemType, StandardAnswerValue.Other);
	if (otherGLNIssue) {
		return true;
	}

	// Only consider specific things as a non-hazard
	return false;
}

symptomMappings['HZ'] = (answers: ReportAnswers, masterData: PremiseDataDocument | undefined): boolean => {
	// Everything but specific non-hazards
	if (isNonHazard(answers, masterData)) {
		return false;
	}

	// This was the previous fallback and is what we'd want if there are no other questions answered
	return true;
};

symptomMappings['NONHZD'] = (_answers: ReportAnswers): boolean => {
	return isNonHazard(_answers, undefined);
};

export function isArcing(answers: ReportAnswers): boolean {
	const arcing = isAnswerValueSelected(answers, ReportQuestion.Emergencies, EquipmentProblemQuestionValue.ArcingWire);
	if (arcing) {
		return true;
	}

	const itemsArcing = getSelectedAnswerStrings(answers, ReportQuestion.Arcing);
	if (isNotEmpty(itemsArcing)) {
		return true;
	}

	return false;
}

symptomMappings['ARC'] = (answers: ReportAnswers): boolean => {
	return isArcing(answers);
};

symptomMappings['CONTACT'] = (answers: ReportAnswers): boolean => {
	const contact = isAnswerValueSelected(answers, ReportQuestion.Emergencies, EmergencyQuestionValue.Contact);
	if (!contact) {
		return false;
	}

	return true;
};

symptomMappings['TRAPPED'] = (answers: ReportAnswers): boolean => {
	const trapped = isAnswerValueSelected(answers, ReportQuestion.Emergencies, EmergencyQuestionValue.Trapped);
	if (!trapped) {
		return false;
	}

	return true;
};

export function areOnlyInterruptibleAppliancesSelected(answers: ReportAnswers): boolean | undefined {
	const selectedAppliances = getSelectedAnswerStrings(answers, ReportQuestion.AppliancesWithoutPower);

	// See if we can rule this out
	if (!includesAny(InterruptibleAppliances, selectedAppliances)) {
		return undefined;
	}

	// Make sure that we haven't selected something that isn't interruptible
	return includesOnly(InterruptibleAppliances, selectedAppliances);
}

function isInterruptibleIssue(answers: ReportAnswers, masterData: PremiseDataDocument | undefined): boolean {
	// Check if this is a power issue
	const powerIssue = isPowerIssue(answers, masterData);
	if (!powerIssue) {
		return false;
	}

	// Check for partial outage
	const partialOutage = isAnswerValueSelected(answers, ReportQuestion.PowerIssue, PowerIssue.PartialOutage);
	if (!partialOutage) {
		return false;
	}

	// See if only interruptible appliances are selected
	const onlyInterruptible = areOnlyInterruptibleAppliancesSelected(answers);
	if (!onlyInterruptible) {
		// If we can't rule it out, we assume it's an interruptible issue
		return false;
	}

	// See if the premise has interruptible service
	const interruptible = hasInterruptibleService(masterData);
	if (!interruptible) {
		return false;
	}

	return true;
}

symptomMappings['INTERRUPTIBLE'] = (answers: ReportAnswers, masterData: PremiseDataDocument | undefined): boolean => {
	return isInterruptibleIssue(answers, masterData);
};

symptomMappings['ONELEG'] = (answers: ReportAnswers, masterData: PremiseDataDocument | undefined): boolean => {
	const interruptible = isInterruptibleIssue(answers, masterData);
	if (interruptible) {
		// If this is an interruptible issue, we don't want to match one leg
		return false;
	}

	const partialOutage = isAnswerValueSelected(answers, ReportQuestion.PowerIssue, PowerIssue.PartialOutage);
	if (!partialOutage) {
		return false;
	}

	// TODO: one leg needs to exclude possible interruptible

	return true;
};

symptomMappings['HIGHVOL'] = (answers: ReportAnswers, masterData: PremiseDataDocument | undefined): boolean => {
	const powerIssue = isPowerIssue(answers, masterData);
	if (!powerIssue) {
		return false;
	}

	const constantBright = isAnswerValueSelected(answers, ReportQuestion.PowerIssue, PowerIssue.ConstantBright);
	if (!constantBright) {
		return false;
	}

	return true;
};

symptomMappings['LOWVOL'] = (answers: ReportAnswers, masterData: PremiseDataDocument | undefined): boolean => {
	const powerIssue = isPowerIssue(answers, masterData);
	if (!powerIssue) {
		return false;
	}

	const lowVoltage = isAnswerValueSelected(answers, ReportQuestion.PowerIssue, [
		PowerIssue.DimOrBrightLights,
		PowerIssue.ConstantDim,
		PowerIssue.LightGoingBrightToDim,
	]);
	if (!lowVoltage) {
		return false;
	}

	return true;
};

symptomMappings['FLICKER'] = (answers: ReportAnswers, masterData: PremiseDataDocument | undefined): boolean => {
	const powerIssue = isPowerIssue(answers, masterData);
	if (!powerIssue) {
		return false;
	}

	const flickeringLights = isAnswerValueSelected(answers, ReportQuestion.PowerIssue, PowerIssue.FlickeringLights);
	if (!flickeringLights) {
		return false;
	}

	return true;
};

symptomMappings['TRAFCSIGPF'] = (answers: ReportAnswers): boolean => {
	// Police / fire must be standing by
	const policeFireStandingBy = meetsPoliceFireStandbyCriteria(answers);
	if (!policeFireStandingBy) {
		return false;
	}

	// Something is wrong with the traffic signal
	const trafficSignal = isAnswerValueSelected(answers, ReportQuestion.Emergencies, [
		EmergencyQuestionValue.TrafficSignalOut,
		EmergencyQuestionValue.TrafficSignalBlinking,
	]);
	if (!trafficSignal) {
		return false;
	}

	return true;
};

symptomMappings['PTFIRE'] = (answers: ReportAnswers): boolean => {
	// Pole or transformer is on fire
	const poleOrTransformerFire = isAnswerValueSelected(answers, ReportQuestion.Fire, [
		FireQuestionValue.Pole,
		FireQuestionValue.OverheadTransformer,
		FireQuestionValue.UndergroundTransformer,
	]);
	if (!poleOrTransformerFire) {
		return false;
	}

	return true;
};

symptomMappings['BP'] = (answers: ReportAnswers): boolean => {
	// TODO: this still doesn't seem right, but that's what CRM is doing

	// Ignore broken pole if it's coming from the GLN path
	const glnProblem = isQuestionAnswered(answers, ReportQuestion.GLNProblemType);
	if (glnProblem) {
		return false;
	}

	const brokenPole = isAnswerValueSelected(answers, ReportQuestion.PoleIssue, [
		PoleIssueQuestionValue.Broken,
		PoleIssueQuestionValue.DangerFalling,
	]);
	const leaning = isAnswerValueSelected(answers, ReportQuestion.PoleIssue, [
		PoleIssueQuestionValue.Broken,
		PoleIssueQuestionValue.Leaning,
	]);
	if (!brokenPole && !leaning) {
		return false;
	}

	return true;
};

function isStructureFire(answers: ReportAnswers): boolean | undefined {
	return isAnswerValueSelected(answers, ReportQuestion.Fire, [
		FireQuestionValue.HouseOrBuilding,
		FireQuestionValue.Roof,
	]);
}

symptomMappings['STRUFIREPF'] = (answers: ReportAnswers): boolean => {
	// Has to be reported by police / fire (doesn't matter if they're standing by)
	const policeFire = isPoliceFire(answers);
	if (!policeFire) {
		return false;
	}

	// Building or roof are on fire
	const structureFire = isStructureFire(answers);
	if (!structureFire) {
		return false;
	}

	return true;
};

export function isExposedUnderground(answers: ReportAnswers): boolean {
	// Someone is in contact with an exposed wire
	let exposed = isAnswerValueSelected(answers, ReportQuestion.EquipmentContact, EnergizedContact.UndergroundWire);
	if (exposed) {
		return true;
	}

	// Wire is just exposed
	exposed = isAnswerValueSelected(
		answers,
		ReportQuestion.UndergroundWireIssue,
		UndergroundWireIssueQuestionValue.ExposedWire,
	);
	if (exposed) {
		return true;
	}

	return false;
}

symptomMappings['UGEXP'] = (answers: ReportAnswers): boolean => {
	// Underground wire must be exposed
	const exposed = isExposedUnderground(answers);
	if (!exposed) {
		return false;
	}

	return true;
};

export function doesCallCodeMatchReportV3(
	report: CustomerReportDataV3,
	callCode: string,
	masterData?: PremiseDataDocument,
): boolean | undefined {
	const matchFunction = symptomMappings[callCode];
	if (!matchFunction) {
		return false;
	}

	// Get only the answers for selected categories
	const answers = getFilteredReportAnswers(report);

	return matchFunction(answers, masterData);
}
